import type { CanDoItem, CommunicationTopicMeta } from "../modules/tutor/api/tutor.client";

/**
 * Turn a Can-Do English statement into a short practice topic label for the Communication Tutor.
 */
export function toTopic(statement: string): string {
  return statement
    .replace(/^Can\s+/i, "")
    .replace(/^I can\s+/i, "")
    .replace(/\.$/, "")
    .replace(/^./, (c) => c.toUpperCase());
}

/** One Communication Tutor practice row (topic id + primary can-do for scoring). */
export type PracticeTopicRow = {
  id: string;
  title: string;
  description?: string;
  practiceCanDoId: string;
};

/**
 * Build practice rows from teacher-defined communication topics + can-dos (lesson slot 0).
 */
export function practiceTopicsForCommunicationTutor(data: {
  communication_topics: CommunicationTopicMeta[];
  can_dos: CanDoItem[];
}): PracticeTopicRow[] {
  const meta = data.communication_topics || [];
  const cds = data.can_dos || [];
  const byTopic = new Map<string, CanDoItem[]>();
  for (const cd of cds) {
    const tid = cd.communication_topic_id || "_ungrouped";
    if (!byTopic.has(tid)) byTopic.set(tid, []);
    byTopic.get(tid)!.push(cd);
  }
  const items: PracticeTopicRow[] = [];
  for (const t of meta) {
    const group = byTopic.get(t.id) || [];
    if (group.length === 0) continue;
    const first = group[0];
    items.push({
      id: t.id,
      title: (t.title_en || "").trim() || toTopic((first.statement_en || "").trim() || first.statement_ta),
      description: t.title_ta?.trim() || undefined,
      practiceCanDoId: first.id,
    });
  }
  const ungrouped = byTopic.get("_ungrouped") || [];
  for (const cd of ungrouped) {
    items.push({
      id: cd.id,
      title: toTopic((cd.statement_en || "").trim() || cd.statement_ta || "Practice"),
      description: cd.statement_ta?.trim() || undefined,
      practiceCanDoId: cd.id,
    });
  }
  return items;
}
